import React from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Calendar, CheckCircle, Clock, Users } from 'lucide-react';
import { roadmap } from '../data/newContent';

const RoadmapSection = () => {
  const statusColors = {
    'In Progress': 'from-blue-500 to-cyan-600',
    'Planned': 'from-purple-500 to-pink-600',
    'Future': 'from-gray-500 to-slate-600'
  };

  const statusBadgeColors = {
    'In Progress': 'border-blue-400 text-blue-400',
    'Planned': 'border-purple-400 text-purple-400',
    'Future': 'border-gray-400 text-gray-400'
  };

  return (
    <section id="roadmap" className="py-20 bg-slate-800 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 opacity-10">
        {Array.from({ length: 20 }).map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-2 h-2 bg-cyan-400 rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              scale: [1, 1.5, 1],
              opacity: [0.3, 1, 0.3]
            }}
            transition={{
              duration: Math.random() * 3 + 2,
              repeat: Infinity,
              delay: Math.random() * 2
            }}
          />
        ))}
      </div>

      <div className="container mx-auto px-6 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Development <span className="text-cyan-400">Roadmap</span>
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Clear milestones with defined budget ranges, exit criteria, and use of funds transparency
          </p>
        </motion.div>

        {/* Timeline */}
        <div className="relative">
          {/* Vertical line */}
          <div className="absolute left-8 md:left-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-cyan-400 via-purple-400 to-gray-400 hidden md:block" />

          <div className="space-y-12">
            {roadmap.map((milestone, index) => {
              const isEven = index % 2 === 0;
              
              return (
                <motion.div
                  key={milestone.title}
                  initial={{ opacity: 0, x: isEven ? -50 : 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.2, duration: 0.6 }}
                  viewport={{ once: true }}
                  className={`flex items-center gap-8 ${isEven ? 'md:flex-row' : 'md:flex-row-reverse'}`}
                >
                  {/* Timeline dot */}
                  <div className="hidden md:flex absolute left-1/2 -translate-x-1/2 w-16 h-16 rounded-full bg-gradient-to-r from-slate-800 to-slate-900 border-4 border-cyan-400 items-center justify-center z-10">
                    <span className="text-cyan-400 font-bold">{index + 1}</span>
                  </div>

                  {/* Content card */}
                  <div className={`w-full md:w-[calc(50%-4rem)] ${isEven ? 'md:pr-12' : 'md:pl-12'}`}>
                    <Card className={`bg-gradient-to-br ${statusColors[milestone.status]} border-0 shadow-xl`}>
                      <CardHeader>
                        <div className="flex items-start justify-between mb-2">
                          <CardTitle className="text-white text-xl">{milestone.title}</CardTitle>
                          <Badge variant="outline" className={`${statusBadgeColors[milestone.status]} bg-slate-900/50`}>
                            {milestone.status}
                          </Badge>
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div className="flex items-center gap-2 text-white/90">
                            <Clock size={16} />
                            <span className="text-sm">
                              {milestone.duration_display || `${milestone.duration_weeks} weeks`}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 text-white/90">
                            <Users size={16} />
                            <span className="text-sm">{milestone.owner}</span>
                          </div>
                        </div>

                        <div className="pt-4 border-t border-white/20">
                          <div className="flex items-start gap-2">
                            <CheckCircle className="text-white/90 flex-shrink-0 mt-0.5" size={16} />
                            <div>
                              <p className="text-xs font-semibold text-white/80 mb-1">Exit Criteria:</p>
                              <p className="text-sm text-white/90">{milestone.exit_criteria}</p>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* Use of Funds Summary */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="mt-16"
        >
          <Card className="bg-slate-900/50 border-cyan-400/20">
            <CardHeader>
              <CardTitle className="text-2xl text-white text-center">Use of Funds Allocation</CardTitle>
              <CardDescription className="text-center text-gray-300">
                Transparent budget breakdown across key development areas
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                <div className="text-center p-4 bg-blue-900/20 rounded-lg border border-blue-400/20">
                  <div className="text-3xl font-bold text-blue-400 mb-2">35%</div>
                  <div className="text-sm text-gray-300">R&D & Engineering</div>
                </div>
                <div className="text-center p-4 bg-green-900/20 rounded-lg border border-green-400/20">
                  <div className="text-3xl font-bold text-green-400 mb-2">25%</div>
                  <div className="text-sm text-gray-300">Pilot Programs</div>
                </div>
                <div className="text-center p-4 bg-purple-900/20 rounded-lg border border-purple-400/20">
                  <div className="text-3xl font-bold text-purple-400 mb-2">20%</div>
                  <div className="text-sm text-gray-300">Infrastructure</div>
                </div>
                <div className="text-center p-4 bg-orange-900/20 rounded-lg border border-orange-400/20">
                  <div className="text-3xl font-bold text-orange-400 mb-2">20%</div>
                  <div className="text-sm text-gray-300">Go-to-Market</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </section>
  );
};

export default RoadmapSection;
